import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ComponentProviderProps } from '@business/interfaces/component-provider';
import { routePaths } from '@utils/constants/routePaths';
import {
  getToken,
  setUserNotificationData
} from '@ports/common/sessionStateSlice';
import {
  getFirstLogin,
  getIsAuthenticated
} from '@ports/common/authStateSlice';
import { onMessage } from 'firebase/messaging';
import { messaging } from '@pushNotifications/firebase';

const authUrls = [
  '/',
  routePaths.SignUp,
  routePaths.SignIn,
  routePaths.ForgotPassword,
  routePaths.TwoFactorAuthentication
];

/*
 * This component is used redirect the user to corresponding page if user is logged in
 */
export function AuthGuard({ children }: Readonly<ComponentProviderProps>) {
  const dispatch = useDispatch();
  const storeToken = useSelector(getToken);
  const sessionToken = sessionStorage.getItem('token');
  const token = storeToken ? storeToken : sessionToken;

  const isAuthenticated = useSelector(getIsAuthenticated);
  const isFirstLogin = useSelector(getFirstLogin);

  useEffect(() => {
    if (
      !token &&
      !authUrls.includes(location?.pathname) &&
      !location.pathname.startsWith(routePaths.ResetPassword)
    ) {
      window.location.href = '/';
    } else if (
      token &&
      isAuthenticated &&
      authUrls.includes(location?.pathname)
    ) {
      if (isFirstLogin) {
        window.location.href = routePaths.Subscription;
      } else {
        window.location.href = routePaths.Photos;
      }
    }
  }, [location.pathname, token, isAuthenticated, isFirstLogin]);

  const notificationMessageHandler = useCallback(
    (payload: any) => {
      console.log('📨 Foreground message received:', payload);
      if (document.visibilityState === 'visible') {
        dispatch(
          setUserNotificationData({
            page: payload?.data?.mediaSource ?? 'photos',
            notificationData: payload?.data
          })
        );
      }
    },
    [dispatch]
  );

  // Setup Firebase foreground message listener for authenticated users
  useEffect(() => {
    if (!token || !isAuthenticated || !messaging) return undefined;

    const unsubscribe = onMessage(messaging, notificationMessageHandler);
    return () => {
      // Cleanup listener on unmount or when user logs out
      if (typeof unsubscribe !== 'undefined') {
        unsubscribe();
      }
    };
  }, [token, isAuthenticated]);

  return <>{children}</>;
}
